import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { savePDF } from './savePDF';

const PW = 210, PH = 297, ML = 12, MR = 12, CW = PW - ML - MR;

const C = {
  maroon:    [142, 44,  44],
  orange:    [212, 120, 28],
  green:     [34,  139, 87],
  amber:     [230, 126, 34],
  red:       [192, 57,  43],
  blue:      [41,  128, 185],
  purple:    [142, 68,  173],
  teal:      [22,  160, 133],
  indigo:    [63,  81,  181],
  dark:      [44,  62,  80],
  gray:      [108, 117, 125],
  lightGray: [225, 229, 233],
  white:     [255, 255, 255],
  bgCard:    [249, 250, 251],
};

const lighten = (rgb, f = 0.90) => rgb.map(v => Math.round(v + (255 - v) * f));

const fmt = d =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const pct = (n, d) => d > 0 ? `${Math.round((n / d) * 100)}%` : '0%';

const getBase64Image = src =>
  new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const checkPage = (doc, y, needed = 20) => {
  if (y + needed > PH - 16) { doc.addPage(); return 16; }
  return y;
};

const sectionTitle = (doc, title, y, color = C.maroon) => {
  y = checkPage(doc, y, 16);
  doc.setFillColor(...color);
  doc.rect(ML, y, 3.5, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...color);
  doc.text(title.toUpperCase(), ML + 6, y + 5);
  doc.setDrawColor(...C.lightGray);
  doc.setLineWidth(0.25);
  doc.line(ML, y + 9, PW - MR, y + 9);
  return y + 13;
};

const statCards = (doc, cards, y) => {
  const n = Math.min(cards.length, 4);
  const gap = 3, cardW = (CW - gap * (n - 1)) / n, cardH = 22;
  y = checkPage(doc, y, cardH + 6);
  cards.forEach((card, i) => {
    const x = ML + i * (cardW + gap);
    doc.setFillColor(...C.bgCard);
    doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, 'F');
    doc.setFillColor(...card.color);
    doc.roundedRect(x, y, cardW, 2, 1, 1, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...card.color);
    doc.text(String(card.value), x + cardW / 2, y + 13, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...C.gray);
    const lines = doc.splitTextToSize(card.label, cardW - 3);
    doc.text(lines[0] || '', x + cardW / 2, y + 19, { align: 'center' });
  });
  return y + cardH + 6;
};

const insightBox = (doc, lines, y, color = C.blue) => {
  const lineH = 4.8, padV = 3.5, boxH = lines.length * lineH + padV * 2;
  y = checkPage(doc, y, boxH + 6);
  doc.setFillColor(...lighten(color, 0.92));
  doc.roundedRect(ML, y, CW, boxH, 1.5, 1.5, 'F');
  doc.setFillColor(...color);
  doc.rect(ML, y, 2.5, boxH, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.dark);
  lines.forEach((line, i) => doc.text(line, ML + 5, y + padV + 3.5 + i * lineH));
  return y + boxH + 6;
};

const miniBarChart = (doc, items, y, color, maxLabelW = 55) => {
  if (!items?.length) return y;
  const barH = 6, gap = 3, valW = 14, barAreaW = CW - maxLabelW - valW - 4;
  const maxVal = Math.max(...items.map(x => x.value), 1);
  y = checkPage(doc, y, items.length * (barH + gap) + 4);
  items.forEach((item, i) => {
    const ry = y + i * (barH + gap);
    const label = item.label.length > 28 ? item.label.slice(0, 27) + '…' : item.label;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...C.dark);
    doc.text(label, ML, ry + barH - 1);
    doc.setFillColor(...C.lightGray);
    doc.roundedRect(ML + maxLabelW, ry, barAreaW, barH, 1.5, 1.5, 'F');
    if (item.value > 0) {
      const bw = Math.max((item.value / maxVal) * barAreaW, 2);
      doc.setFillColor(...color);
      doc.roundedRect(ML + maxLabelW, ry, bw, barH, 1.5, 1.5, 'F');
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
    doc.setTextColor(...(item.value > 0 ? color : C.gray));
    doc.text(String(item.value) + (item.suffix || ''), ML + maxLabelW + barAreaW + 3, ry + barH - 1);
  });
  return y + items.length * (barH + gap) + 5;
};

const pageHeader = async (doc, logoBase64, collegeName, reportType, periodLabel, scopeLabel) => {
  const hH = 38;
  doc.setFillColor(...C.maroon);
  doc.rect(0, 0, PW, hH, 'F');
  let textX = ML;
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', ML, 5, 22, 22); textX = ML + 26; } catch (_) {}
  }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.white);
  const nameLines = doc.splitTextToSize(collegeName, PW - textX - MR - 42);
  doc.text(nameLines, textX, 12);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(220, 190, 190);
  doc.text(`${scopeLabel}  ·  Period: ${periodLabel}  ·  Generated: ${fmt(new Date())}`, textX, 12 + nameLines.length * 5 + 2);

  const badge = `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`;
  const bW = 38, bH = 8;
  doc.setFillColor(...C.orange);
  doc.roundedRect(PW - MR - bW, 6, bW, bH, 2, 2, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...C.white);
  doc.text(badge, PW - MR - bW / 2, 11.5, { align: 'center' });

  doc.setDrawColor(...C.orange); doc.setLineWidth(1); doc.line(0, hH, PW, hH);
  return hH + 8;
};

const pageFooter = (doc, p, total, collegeName) => {
  const fy = PH - 8;
  doc.setDrawColor(...C.lightGray); doc.setLineWidth(0.2); doc.line(ML, fy - 3, PW - MR, fy - 3);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...C.gray);
  doc.text(collegeName, ML, fy);
  doc.text(`Page ${p} of ${total}`, PW - MR, fy, { align: 'right' });
  doc.text('Confidential — Analytics Report', PW / 2, fy, { align: 'center' });
};

const tableConf = (hColor) => ({
  margin: { left: ML, right: MR },
  headStyles: { fillColor: hColor, textColor: C.white, fontSize: 7.5, fontStyle: 'bold', cellPadding: 2.5 },
  bodyStyles: { fontSize: 7, cellPadding: 2, textColor: C.dark, lineColor: C.lightGray, lineWidth: 0.12 },
  tableLineColor: C.lightGray, tableLineWidth: 0.18,
});

export const generateAnalyticsReport = async ({
  reportType,
  periodLabel,
  scopeLabel,
  summary,
  teacherActivity,
  studentActivity,
  courseBreakdown,
  collegeName,
  logoSrc,
}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logoBase64 = logoSrc ? await getBase64Image(logoSrc) : null;

  let y = await pageHeader(doc, logoBase64, collegeName, reportType, periodLabel, scopeLabel);

  // ── Executive Summary ──────────────────────────────────────────────────────
  y = sectionTitle(doc, 'Executive Summary', y, C.maroon);
  y = statCards(doc, [
    { value: summary.totalStudents, label: 'Total Students', color: C.blue },
    { value: summary.totalTeachers, label: 'Total Teachers/Guides', color: C.purple },
    { value: summary.totalEntries, label: 'Logbook Entries', color: C.orange },
    { value: `${summary.approvalRate}%`, label: 'Approval Rate',
      color: summary.approvalRate >= 70 ? C.green : summary.approvalRate >= 40 ? C.amber : C.red },
  ], y);
  y = statCards(doc, [
    { value: summary.activeStudents, label: 'Active Students', color: C.teal },
    { value: summary.inactiveStudents, label: 'Inactive Students', color: summary.inactiveStudents > 0 ? C.red : C.green },
    { value: summary.approved, label: 'Approved Entries', color: C.green },
    { value: summary.pending, label: 'Pending Review', color: summary.pending > 0 ? C.amber : C.teal },
  ], y);
  y += 2;
  y = statCards(doc, [
    { value: summary.drpTotal, label: 'Total DRP Students', color: C.indigo },
    { value: summary.drpVerified, label: 'Fully Verified DRP', color: C.teal },
    { value: summary.drpCertified, label: 'DRP Certificates Issued', color: C.maroon },
    { value: summary.totalCourses || '—', label: 'Active Courses', color: C.orange },
  ], y);

  // ── Key Insights ──────────────────────────────────────────────────────────
  y = sectionTitle(doc, 'Key Insights & Analysis', y, C.blue);
  const avgEntries = summary.totalStudents > 0 ? (summary.totalEntries / summary.totalStudents).toFixed(1) : '0';
  const topTeacher = teacherActivity?.[0];
  const noSubmitCount = summary.totalStudents - summary.activeStudents;
  const insights = [
    `• ${summary.totalEntries} logbook entries submitted by ${summary.activeStudents} of ${summary.totalStudents} students (${pct(summary.activeStudents, summary.totalStudents)} participation, avg ${avgEntries} entries/student).`,
    topTeacher?.reviewed > 0
      ? `• Most active faculty: ${topTeacher.name} with ${topTeacher.reviewed} verification(s) in this period.`
      : '• No faculty has performed verifications during this period.',
    noSubmitCount > 0
      ? `• ${noSubmitCount} student(s) have not submitted any entries — follow-up recommended.`
      : '• All enrolled students have submitted at least one entry.',
    `• Overall approval rate: ${summary.approvalRate}% (${summary.approved} approved of ${summary.totalEntries} entries).`,
    summary.drpCertified > 0
      ? `• ${summary.drpCertified} DRP certificates issued; ${summary.drpVerified} students are fully verified.`
      : '• No DRP certificates issued in this period.',
  ].filter(Boolean);
  y = insightBox(doc, insights, y, C.blue);

  // ── Department/Course Breakdown ──────────────────────────────────────────
  if (courseBreakdown?.length > 0) {
    y = sectionTitle(doc, 'Department / Course Breakdown', y, C.indigo);
    y = checkPage(doc, y, 20);
    autoTable(doc, {
      startY: y,
      ...tableConf(C.indigo),
      alternateRowStyles: { fillColor: lighten(C.indigo, 0.95) },
      head: [['Department / Course', 'Code', 'Students', 'Teachers', 'Logbook Entries', 'Approved', 'Rate']],
      body: courseBreakdown.map(d => [
        d.courseName, d.courseCode || '—',
        d.studentCount, d.teacherCount, d.logbookCount,
        d.approvedCount,
        `${d.approvalRate}%`
      ]),
      columnStyles: {
        0: { cellWidth: 52 }, 1: { cellWidth: 18, halign: 'center' },
        2: { cellWidth: 18, halign: 'center' }, 3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' }, 5: { cellWidth: 20, halign: 'center' },
        6: { cellWidth: 15, halign: 'center' },
      },
      didParseCell: data => {
        if (data.section !== 'body' || data.column.index !== 6) return;
        const v = parseInt(data.cell.raw);
        data.cell.styles.textColor = v >= 70 ? C.green : v >= 40 ? C.amber : C.red;
        data.cell.styles.fontStyle = 'bold';
      }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Faculty Activity ───────────────────────────────────────────────────────
  if (teacherActivity?.length > 0) {
    y = sectionTitle(doc, 'Faculty / Guide Activity', y, C.purple);
    const topItems = teacherActivity.slice(0, 12).map(t => ({ label: t.name, value: t.reviewed }));
    y = miniBarChart(doc, topItems, y, C.purple);
    y = checkPage(doc, y, 20);
    autoTable(doc, {
      startY: y,
      ...tableConf(C.purple),
      alternateRowStyles: { fillColor: lighten(C.purple, 0.95) },
      head: [['Faculty Name', 'Role', 'Verified This Period', 'Pending', 'Status']],
      body: teacherActivity.map(t => [
        t.name,
        t.isHOD ? 'HOD' : 'Guide',
        t.reviewed,
        t.pending,
        t.reviewed === 0 ? 'Inactive' : t.reviewed >= 5 ? 'Active' : 'Low Activity'
      ]),
      columnStyles: {
        0: { cellWidth: 52 }, 1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 30, halign: 'center' }, 3: { cellWidth: 25, halign: 'center' },
        4: { cellWidth: 55, halign: 'center' },
      },
      didParseCell: data => {
        if (data.section !== 'body' || data.column.index !== 4) return;
        const v = String(data.cell.raw);
        data.cell.styles.textColor = v === 'Active' ? C.green : v === 'Inactive' ? C.red : C.amber;
        data.cell.styles.fontStyle = 'bold';
      }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Student Activity ──────────────────────────────────────────────────────
  if (studentActivity?.length > 0) {
    y = sectionTitle(doc, 'Student Logbook Activity', y, C.teal);
    y = checkPage(doc, y, 20);
    autoTable(doc, {
      startY: y,
      ...tableConf(C.teal),
      alternateRowStyles: { fillColor: lighten(C.teal, 0.95) },
      head: [['Student Name', 'Reg. No.', 'Course', 'Submitted', 'Approved', 'Rate', 'Status']],
      body: studentActivity.map(s => [
        s.name, s.registrationNumber || '—', s.course || '—',
        s.submitted, s.approved, `${s.rate}%`,
        s.submitted === 0 ? 'No Activity' : s.rate >= 70 ? 'Good' : s.rate >= 40 ? 'Average' : 'Needs Attention'
      ]),
      columnStyles: {
        0: { cellWidth: 42 }, 1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 30 }, 3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' }, 5: { cellWidth: 14, halign: 'center' },
        6: { cellWidth: 38, halign: 'center' },
      },
      didParseCell: data => {
        if (data.section !== 'body' || data.column.index !== 6) return;
        const v = String(data.cell.raw);
        data.cell.styles.textColor = v === 'Good' ? C.green : v === 'No Activity' ? C.red : C.amber;
        data.cell.styles.fontStyle = 'bold';
      }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Footers ────────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p); pageFooter(doc, p, totalPages, collegeName);
  }

  const dateStr = new Date().toISOString().slice(0, 10);
  const typeLabel = reportType.charAt(0).toUpperCase() + reportType.slice(1);
  await savePDF(doc, `Analytics_${typeLabel}_Report_${dateStr}.pdf`);
};
